import { buildApiUrl } from '../config/api';
import tokenManager from './tokenManager';

/**
 * Enhanced fetch wrapper that automatically includes security tokens
 */
export const apiCall = async (endpoint, options = {}) => {
  let retryCount = 0;
  const maxRetries = 2;

  while (retryCount <= maxRetries) {
    try {
      // Get authentication headers
      const authHeaders = await tokenManager.getAuthHeaders();
      
      // Merge headers
      const headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      };
      
      // Make the API call
      const response = await fetch(buildApiUrl(endpoint), {
        ...options,
        headers
      });
      
      // Handle token expiration
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'Invalid or missing security token') {
          console.log(`Token validation failed (attempt ${retryCount + 1}/${maxRetries + 1}), retrying...`);
          
          // Clear the token and try again
          tokenManager.clearToken();
          retryCount++;
          
          if (retryCount <= maxRetries) {
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue; // Try again with new token
          }
        }
      }
      
      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} - ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      console.error(`API call error (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
      
      // If this is the last retry, throw the error
      if (retryCount >= maxRetries) {
        throw error;
      }
      
      // For network errors or other issues, wait and retry
      retryCount++;
      await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
    }
  }
};

/**
 * Specialized API call for FormData (like file uploads)
 */
export const apiCallFormData = async (endpoint, formData, options = {}) => {
  let retryCount = 0;
  const maxRetries = 2;

  while (retryCount <= maxRetries) {
    try {
      // Get authentication headers (but don't set Content-Type for FormData)
      const authHeaders = await tokenManager.getAuthHeaders();
      
      // Merge headers (FormData will set its own Content-Type)
      const headers = {
        ...authHeaders,
        ...options.headers
      };
      
      // Make the API call
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers,
        body: formData,
        ...options
      });
      
      // Handle token expiration
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'Invalid or missing security token') {
          console.log(`Token validation failed for FormData (attempt ${retryCount + 1}/${maxRetries + 1}), retrying...`);
          
          // Clear the token and try again
          tokenManager.clearToken();
          retryCount++;
          
          if (retryCount <= maxRetries) {
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue; // Try again with new token
          }
        }
      }
      
      // Don't throw error for 429 (rate limit) - let the calling code handle it
      if (!response.ok && response.status !== 429) {
        throw new Error(`API call failed: ${response.status} - ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      console.error(`FormData API call error (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
      
      // If this is the last retry, throw the error
      if (retryCount >= maxRetries) {
        throw error;
      }
      
      // For network errors or other issues, wait and retry
      retryCount++;
      await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
    }
  }
};

/**
 * GET request with automatic token management
 */
export const apiGet = async (endpoint) => {
  const response = await apiCall(endpoint, { method: 'GET' });
  return response.json();
};

/**
 * POST request with automatic token management
 */
export const apiPost = async (endpoint, data) => {
  const response = await apiCall(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  });
  return response.json();
};

/**
 * PUT request with automatic token management
 */
export const apiPut = async (endpoint, data) => {
  const response = await apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  return response.json();
};

/**
 * DELETE request with automatic token management
 */
export const apiDelete = async (endpoint) => {
  const response = await apiCall(endpoint, { method: 'DELETE' });
  return response.json();
}; 